// Global Instructions Rule Applied!

import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import type { RootState } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../../core/services/utilitiesService.ts'
import { buildReportingGridExcelPayload } from '../../../../state_manager/utils/reportingGridExcelExport.ts'
import { fetchAllContactsForExport } from '../../../shared/table_grid_export/fetchAllFilteredForExport.ts'

import ContactsTable from './ContactsTable.tsx'
import { useContactsData } from '../../hooks/useContactsData.ts'
import { useContactsDrawer } from '../../hooks/useContactsDrawer.ts'
import { useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { TABLE_CONTACTS } from '../../../../state_manager/globals.ts'
import { ContactRecordFields } from '../../../../state_manager/data_models/contact_record.ts'

import ViewToolbar from '../../../../core/components/ViewToolbar/index.tsx'
import type { ViewConfigColumn } from '../../../../core/components/ViewConfig/types.ts'
import { ViewConfig } from '../../../../core/components/ViewConfig/index.ts'
import { useContactsReportId } from '../../../../state_manager/hooks/useContactsReportId.ts'
import { useReportingGridColumnsWithPreferences } from '../../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getReportingGridColumnsForReportId } from '../../../../state_manager/utils/reportingGridColumns.ts'
import { getAddColumnsCatalogForReport } from '../../../../state_manager/utils/reportingColumnCatalog.ts'

export default function ContactsPage() {
  const { getLookupLabel, getLookupOptions } = useLookups()

  const contactsReportId = useContactsReportId()
  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector(
    (state: RootState) => state.globalConfigs?.reporting_column_defaults ?? []
  )
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])

  const {
    contactsFilterOptions,
    contactsData,
    contactsDataLoading,
    contactsFilterOptionsLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    handleFetchFilterOptions,
    handleFetchContacts,
    handleSetFilters,
    handleSetSorting,
    handleLoadMore,
    defaultTableFilters,
    defaultTableSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  } = useContactsData()

  const { handleContactNameClick, handleContactSecondaryNameClick } = useContactsDrawer(contactsData)

  const contactsGridColumns = useReportingGridColumnsWithPreferences(contactsReportId, preferenceRow)

  const viewConfigColumns = contactsGridColumns

  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        contactsReportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'contacts' }
      ),
    [contactsReportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!contactsReportId) return null
    const rows = getReportingGridColumnsForReportId(contactsReportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [contactsReportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of viewConfigColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, viewConfigColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const contactsColumnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  const cellEditErrors: Record<string, string> = {}

  const [exportLoading, setExportLoading] = useState(false)

  const handleExportTable = useCallback(async () => {
    setExportLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const { data, error } = await fetchAllContactsForExport(
        activeSorting,
        activeFilters,
        token,
        contactsReportId
      )
      if (error) {
        showErrorToast(error)
        return
      }
      if (data.length === 0) {
        toast.info('No rows to export')
        return
      }
      const records = data.map((r) => ({ ...r }) as Record<string, unknown>)
      const payload = buildReportingGridExcelPayload(contactsGridColumns, records)
      const filename = `Contacts - ${dayjs().format('YYYY-MM-DD')}.xlsx`
      const result = await exportJsonToExcel({ ...payload, filename }, token)
      if (result.success) {
        toast.success(`${filename} exported successfully`)
      } else {
        showErrorToast(result.error || 'Export failed')
      }
    } finally {
      setExportLoading(false)
    }
  }, [activeSorting, activeFilters, contactsReportId, contactsGridColumns])

  return (
    <>
      <div className='h-full overflow-hidden'>
        <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden'>
          <ViewToolbar
            moduleKey={TABLE_CONTACTS}
            searchPlaceholder='Search Contacts...'
            recordTitleField={ContactRecordFields.contacts__last_name}
            activeFilters={activeFilters}
            currentFilterOptions={contactsFilterOptions}
            filterOptionsLoading={contactsFilterOptionsLoading}
            onRefreshFilterOptions={handleFetchFilterOptions}
            onRefreshData={() => handleFetchContacts(activeSorting, activeFilters)}
            onFilterChange={handleSetFilters}
            defaultFilters={defaultTableFilters}
            onResetFilters={handleResetFilters}
            exportEnabled
            onExport={handleExportTable}
            exportLoading={exportLoading}
            beforeAddSlot={
              <ViewConfig
                moduleTitle='Contacts'
                columns={viewConfigColumns}
                filterOptions={contactsFilterOptions}
                filterOptionsLoading={contactsFilterOptionsLoading}
                addColumnsCatalog={addColumnsCatalog}
                addColumnsTabLabels={{ primary: 'Contact', related: 'Related' }}
                columnLayout={contactsColumnLayout}
                filteringConfig={{
                  currentFilters: activeFilters,
                  defaultFilters: defaultTableFilters,
                  onApplyFilters: async (filters) => {
                    handleSetFilters(filters)
                  },
                  onResetToDefaults: async () => {
                    handleSetFilters(defaultTableFilters)
                  }
                }}
                sortingConfig={{
                  currentSorting: activeSorting,
                  defaultSorting: defaultTableSorting,
                  onApplySorting: async (sorting) => {
                    handleSetSorting(sorting)
                  },
                  onResetToDefaults: async () => {
                    handleSetSorting(defaultTableSorting)
                  }
                }}
              />
            }
          />

          <div className='flex-1 min-h-0 h-full flex flex-col'>
            <ContactsTable
              loading={contactsDataLoading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              currentPage={currentPage}
              cellEditErrors={cellEditErrors}
              activeFilters={activeFilters}
              activeSorting={activeSorting}
              filterOptions={contactsFilterOptions}
              filterOptionsLoading={contactsFilterOptionsLoading}
              gridColumns={contactsGridColumns}
              onLoadMore={handleLoadMore}
              onFiltersChange={(filters) => {
                const filterArray = Object.entries(filters).map(([key, value]) => ({
                  key,
                  value
                }))
                handleSetFilters(filterArray)
              }}
              onSortChange={(sortConfig: any) => {
                if (sortConfig) {
                  handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
                } else {
                  handleSetSorting([])
                }
              }}
              onContactNameClick={handleContactNameClick}
              onContactSecondaryNameClick={handleContactSecondaryNameClick}
              getLookupLabel={getLookupLabel}
              getLookupOptions={getLookupOptions}
            />
          </div>
        </div>
      </div>
    </>
  )
}
